import { PERMISSION_DENIED_CODE } from './permission-base-query';
import { PERMISSION_DENIED_DATA } from './permission-denied-data';

/**
 * Optional per-endpoint substitute payload registry.
 *
 * By default the auto-wrapper substitutes a generic "empty everything"
 * sentinel (`PERMISSION_DENIED_DATA`) into `data` on denial, which satisfies
 * common component patterns (`data?.list?.length === 0`, `data?.items?.map`,
 * scalar render, deep chains) without any per-file change.
 *
 * If a specific endpoint needs an exact response shape (e.g. arithmetic on
 * a counter, identity comparisons against ids), opt out for that endpoint:
 *
 *   registerPermissionDeniedData('getThing', { items: [], total: 0 });
 *
 * Registration is OPTIONAL — only needed when the default sentinel causes a
 * subtle render quirk on a particular page.
 *
 * @type {Map<string, unknown | ((arg: unknown) => unknown)>}
 */
const deniedDataRegistry = new Map();

/**
 * Declares the default payload to substitute into `data` when the named
 * endpoint is denied. `valueOrFactory` may be a static value or a function
 * receiving the original query arg (useful when shape varies by params).
 *
 * @example
 * registerPermissionDeniedData('getHiringRequests', {
 *   hiringPlans: [], total: 0, page: 1, perPage: 10,
 * });
 *
 * @param {string} endpointName - The RTK Query endpoint name (camelCase).
 * @param {unknown | ((arg: unknown) => unknown)} valueOrFactory
 */
export function registerPermissionDeniedData(endpointName, valueOrFactory) {
  deniedDataRegistry.set(endpointName, valueOrFactory);
}

function resolveDeniedData(endpointName, arg) {
  if (deniedDataRegistry.has(endpointName)) {
    const entry = deniedDataRegistry.get(endpointName);
    return typeof entry === 'function' ? entry(arg) : entry;
  }
  return PERMISSION_DENIED_DATA;
}

const QUERY_HOOK_RE = /^use([A-Z]\w*)Query$/;
const LAZY_QUERY_HOOK_RE = /^useLazy([A-Z]\w*)Query$/;
const MUTATION_HOOK_RE = /^use([A-Z]\w*)Mutation$/;

function isPermissionDenied(error) {
  return error?.data?.code === PERMISSION_DENIED_CODE;
}

function patchedQueryResult(result, endpointName, arg) {
  if (!isPermissionDenied(result?.error)) return result;
  const denied = resolveDeniedData(endpointName, arg);
  return {
    ...result,
    isPermissionDenied: true,
    isLoading: false,
    isFetching: false,
    isUninitialized: false,
    isError: false,
    isSuccess: true,
    data: denied,
    error: undefined,
  };
}

function patchedMutationResult(result) {
  if (!isPermissionDenied(result?.error)) return result;
  return {
    ...result,
    isPermissionDenied: true,
    isLoading: false,
    isError: false,
    error: undefined,
  };
}

function endpointNameFromHook(hookName) {
  const m = hookName.match(QUERY_HOOK_RE)
    || hookName.match(LAZY_QUERY_HOOK_RE)
    || hookName.match(MUTATION_HOOK_RE);
  if (!m) return null;
  return m[1].charAt(0).toLowerCase() + m[1].slice(1);
}

function wrapHooks(target) {
  Object.keys(target).forEach((key) => {
    const original = target[key];
    if (typeof original !== 'function') return;
    if (original.__permissionSafe) return;

    if (QUERY_HOOK_RE.test(key)) {
      const endpointName = endpointNameFromHook(key);
      const wrapped = function useSafeQuery(arg, options) {
        const result = original(arg, options);
        return patchedQueryResult(result, endpointName, arg);
      };
      wrapped.__permissionSafe = true;
      target[key] = wrapped;
      return;
    }

    if (LAZY_QUERY_HOOK_RE.test(key)) {
      const endpointName = endpointNameFromHook(key);
      const wrapped = function useSafeLazyQuery(options) {
        const tuple = original(options);
        // RTK lazy tuple: [trigger, result, lastPromiseInfo]
        if (!Array.isArray(tuple) || tuple.length < 2) return tuple;
        const [trigger, result, ...rest] = tuple;
        return [
          trigger,
          patchedQueryResult(result, endpointName, result?.originalArgs),
          ...rest,
        ];
      };
      wrapped.__permissionSafe = true;
      target[key] = wrapped;
      return;
    }

    if (MUTATION_HOOK_RE.test(key)) {
      const wrapped = function useSafeMutation(options) {
        const tuple = original(options);
        if (!Array.isArray(tuple) || tuple.length < 2) return tuple;
        const [trigger, result, ...rest] = tuple;
        return [trigger, patchedMutationResult(result), ...rest];
      };
      wrapped.__permissionSafe = true;
      target[key] = wrapped;
    }
  });
}

/**
 * Patches an RTK Query api slice so every hook it generates (now and in the
 * future via `injectEndpoints`) automatically settles permission-denied
 * results: clears loading flags, suppresses error, and optionally substitutes
 * `data` with the value registered via `registerPermissionDeniedData`.
 *
 * Idempotent. Call once, right after `createApi`.
 *
 * @param {object} apiSlice - The slice returned by `createApi`.
 * @returns {object} The same slice (for chaining).
 */
export function enablePermissionSafeHooks(apiSlice) {
  if (!apiSlice || apiSlice.__permissionSafeHooksEnabled) return apiSlice;
  apiSlice.__permissionSafeHooksEnabled = true;

  wrapHooks(apiSlice);

  const originalInject = apiSlice.injectEndpoints?.bind(apiSlice);
  if (typeof originalInject === 'function') {
    apiSlice.injectEndpoints = (definitions) => {
      const extended = originalInject(definitions);
      wrapHooks(extended);
      wrapHooks(apiSlice);
      return extended;
    };
  }

  return apiSlice;
}

export { isPermissionDenied as isPermissionDeniedResult };
